/**
 * Platform fit of a product — one definition for every surface. Generation,
 * the saves of a dynamic portfolio and the settings editor's product picker
 * all answer these questions and must stay in lockstep.
 *
 * A platform set is explicit membership: no entries means member of NO
 * platform.
 */

/**
 * A goal with a platform takes only products tagged to it; a goal without one
 * takes only untagged products — the rule dynamic and model portfolios resolve
 * by. (The custom-portfolio builder's `productAvailableUnderPlatform` is
 * looser: it offers every product to a goal without a platform.) The saves of
 * a dynamic portfolio enforce the same rule through
 * `productFitsDynamicPortfolioPlatforms`.
 */
export function productOnGoalPlatform(
  productPlatformIds: readonly string[],
  goalPlatformId: string | null,
): boolean {
  return goalPlatformId
    ? productPlatformIds.includes(goalPlatformId)
    : productPlatformIds.length === 0;
}

/**
 * QL-584 — whether generation can ever use a product in a dynamic portfolio
 * with this platform membership: the product must be on the platform of every
 * goal the portfolio serves. A tagged portfolio serves goals on each of its
 * platforms; an untagged one serves goals without a platform, which take only
 * untagged products. Built on the generator's own rule so the two cannot drift.
 */
export function productFitsDynamicPortfolioPlatforms(
  productPlatformIds: readonly string[],
  portfolioPlatformIds: readonly string[],
): boolean {
  const goalPlatformIds = portfolioPlatformIds.length > 0 ? portfolioPlatformIds : [null];
  return goalPlatformIds.every((goalPlatformId) =>
    productOnGoalPlatform(productPlatformIds, goalPlatformId),
  );
}
